// web-staking-estimator-frontend/src/services/map/screenshotService.js

/**
 * Helper function to sleep/wait
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise} Resolves after delay
 */
export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Hide or show map overlays (search bar, controls, etc.)
 * @param {boolean} flag - True to hide, false to restore
 */
export function hideOverlays(flag) {
  const selectors = [".map-overlay", ".mapControls"];
  for (const sel of selectors) {
    document.querySelectorAll(sel).forEach((el) => {
      if (flag) {
        el.__prevVisibility = el.style.visibility;
        el.style.visibility = "hidden";
      } else {
        el.style.visibility = el.__prevVisibility || "";
        try {
          delete el.__prevVisibility;
        } catch {}
      }
    });
  }
}

/**
 * Get snapshot of basemap layer status for debugging
 * @param {object} view - MapView
 * @returns {object} Basemap status snapshot
 */
export function basemapSnapshot(view) {
  const bm = view?.map?.basemap;
  const toEntry = (l) => ({
    title: l?.title ?? null,
    id: l?.id ?? null,
    type: l?.type ?? null,
    loadStatus: l?.loadStatus ?? null,
    loaded: !!l?.loaded,
    visible: !!l?.visible,
  });
  return {
    viewState: view?.state,
    updating: !!view?.updating,
    baseCount: bm?.baseLayers?.length ?? 0,
    refCount: bm?.referenceLayers?.length ?? 0,
    base: bm?.baseLayers?.items?.map(toEntry) ?? [],
    ref: bm?.referenceLayers?.items?.map(toEntry) ?? [],
  };
}

/**
 * Wait until basemap layerViews finish updating (prevents black screenshots)
 * @param {object} view - MapView
 * @param {number} timeoutMs - Maximum time to wait
 * @param {boolean} debug - Enable debug logging
 * @returns {Promise<boolean>} True if ready, false if timeout
 */
export async function waitBasemapLayerViewsReady(view, timeoutMs = 12000, debug = false) {
  const bm = view?.map?.basemap;
  if (!bm) return false;

  const layers = [
    ...(bm.baseLayers?.items ?? []),
    ...(bm.referenceLayers?.items ?? []),
  ].filter(Boolean);

  const layerViews = [];
  for (const lyr of layers) {
    try {
      const lv = await view.whenLayerView(lyr);
      layerViews.push(lv);
    } catch {
      /* ignore */
    }
  }

  let stableTicks = 0;
  const start = Date.now();
  
  while (Date.now() - start < timeoutMs) {
    const states = layerViews.map((lv) => !!lv && lv.updating === false);
    const allStable = states.length > 0 && states.every(Boolean) && view.updating === false;

    if (debug) {
      console.log("[pdf-layers] tick", {
        allStable,
        viewUpdating: !!view.updating,
        states,
        snapshot: basemapSnapshot(view),
      });
    }

    if (allStable) {
      stableTicks += 1;
      if (stableTicks >= 2) return true; // require 2 consecutive stable ticks
    } else {
      stableTicks = 0;
    }
    
    await sleep(150);
  }
  
  return false;
}

/**
 * Get overlay readiness stats from view
 * @param {object} view - MapView
 * @returns {object} Overlay statistics
 */
export function getOverlayStats(view) {
  return {
    ts: Number(view?.__overlayLastDrawTs || 0),
    layers: Number(view?.__overlayLayersCount || 0),
    graphics: Number(view?.__overlayGraphicsCount || 0),
  };
}

/**
 * Wait for overlay to have drawn at least minGraphics items after this call begins
 * @param {object} view - MapView
 * @param {number} timeoutMs - Maximum time to wait
 * @param {number} minGraphics - Minimum graphics count
 * @param {boolean} debug - Enable debug logging
 * @returns {Promise<boolean>} True if ready, false if timeout
 */
export async function waitOverlayReady(view, timeoutMs = 10000, minGraphics = 1, debug = false) {
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    // Check for actual overlay GraphicsLayers (distributionLines, serviceArea, etc.)
    const overlayLayers = view?.map?.layers?.items?.filter(l => 
      l?.id && (l.id.startsWith('distributionLines-') || l.id.startsWith('serviceArea-'))
    ) || [];
    
    const totalGraphics = overlayLayers.reduce((sum, layer) => {
      const count = layer?.graphics?.length || 0;
      return sum + count;
    }, 0);
    
    const ready = overlayLayers.length > 0 && totalGraphics >= minGraphics;

    if (debug) {
      console.log("[overlay-wait] tick", { 
        layerCount: overlayLayers.length,
        totalGraphics,
        minGraphics,
        ready,
        elapsed: Date.now() - start
      });
    }

    if (ready) return true;
    await sleep(120);
  }
  
  return false;
}

/**
 * Convert PNG data URL to JPEG at target width
 * @param {string} dataUrl - PNG data URL
 * @param {number} targetWidth - Target width in pixels
 * @param {number} quality - JPEG quality (0-1)
 * @returns {Promise<string>} JPEG data URL
 */
export function pngDataUrlToJpeg(dataUrl, targetWidth = 640, quality = 0.7) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        const scale = targetWidth / img.width;
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        const jpeg = canvas.toDataURL("image/jpeg", quality);
        resolve(jpeg);
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    } catch {
      resolve(dataUrl);
    }
  });
}
